// src/crm/ugc/hooks.ts
//
// Données de l'admin du programme UGC (spec § 6, § 9.3) : une lecture ou une mutation par route
// de functions/api/ugc/**. Toutes les clés commencent par ['ugc'] ; chaque mutation invalide
// ['ugc'] (compteurs des onglets et du Cockpit compris).

import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { ApiClientError, api } from '../../lib/api'
import type {
  AdminPaymentsView,
  AdminPublicationItem,
  AdminReferralItem,
  AdminVideoDetail,
  AdminVideoQueueItem,
  AdminChatListItem,
  AdminChatThread,
  AdminVideoThread,
  ApplicationStatus,
  ConversationEmailOutcome,
  CorrectionReferral,
  MemberPatch,
  ReviewDecision,
  ReviewStage,
  SettlementKind,
  UgcApplication,
  UgcChatMessage,
  UgcMember,
  UgcMessage,
  UgcOverview,
  UgcPayout,
  UgcPublication,
  UgcSettings,
  UgcSettlement,
  UgcVideo,
  VideoStatus,
} from '../../lib/ugcTypes'

/** Candidature de la liste de l'admin : avec le nom du parrain, s'il existe encore. */
export type AdminApplication = UgcApplication & { referrerName: string | null }

/** `queue` : idées et brouillons à traiter ; `all` : « Toutes les vidéos ». */
export type UgcVideosView = 'queue' | 'all'
export type UgcVideosFilters = { memberId?: string; status?: VideoStatus }

/** `to_validate` : captures à valider ; `upcoming` : relevés à venir ; `all` : toutes. */
export type UgcPublicationsView = 'to_validate' | 'upcoming' | 'all'


export const ugcKeys = {
  all: ['ugc'] as const,
  overview: (appId: string) => ['ugc', 'overview', appId] as const,
  applications: (status: ApplicationStatus) => ['ugc', 'applications', status] as const,
  /** Toutes les listes de vidéos (file et « Toutes les vidéos »), quels que soient leurs filtres. */
  videoLists: ['ugc', 'videos'] as const,
  videos: (view: UgcVideosView, filters: UgcVideosFilters) => ['ugc', 'videos', view, filters] as const,
  video: (id: string) => ['ugc', 'video', id] as const,
  thread: (id: string) => ['ugc', 'thread', id] as const,
  chats: ['ugc', 'chats'] as const,
  chat: (memberId: string) => ['ugc', 'chat', memberId] as const,
  publications: (view: UgcPublicationsView) => ['ugc', 'publications', view] as const,
  payments: ['ugc', 'payments'] as const,
  referrals: ['ugc', 'referrals'] as const,
  settings: ['ugc', 'settings'] as const,
  members: (creatorId: string | null) => ['ugc', 'members', creatorId] as const,
}

function query(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) if (value !== undefined) search.set(key, value)
  const text = search.toString()
  return text ? `?${text}` : ''
}

/** Mutation qui invalide toutes les lectures du programme une fois réussie. */
function useUgcMutation<TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>,
): UseMutationResult<TData, Error, TVariables> {
  const queryClient = useQueryClient()
  return useMutation<TData, Error, TVariables>({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ugcKeys.all })
    },
  })
}

// ── Relecture automatique ────────────────────────────────────────────────────

/** Compteurs, liste des conversations et conversation ouverte : relus toutes les 30 s. */
export const LIVE_REFRESH_MS = 30_000

/**
 * Relecture des lectures « vivantes » : toutes les 30 s tant que l'onglet est visible (pas en
 * arrière-plan) et au retour sur l'onglet ; react-query ne lance jamais deux lectures d'une même clé
 * à la fois, et une lecture ratée garde les données affichées (les écrans n'affichent l'erreur
 * qu'à défaut de données). Une session finie (401) arrête la relecture.
 */
const LIVE = {
  refetchInterval: (query: { state: { error: unknown } }): number | false =>
    query.state.error instanceof ApiClientError && query.state.error.status === 401 ? false : LIVE_REFRESH_MS,
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: true,
} as const

/**
 * `?read=1` sur la seule lecture qui suit un choix explicite (ouverture d'une conversation choisie,
 * nouveau clic) : jamais sur une relecture automatique ni après une mutation. `markRead` vrai au
 * montage, ou qui le devient, arme la lecture suivante ; `request()` l'arme avant un `refetch()`.
 * Une lecture qui marquait et échoue réarme le marquage (la suivante le porte) ; `useMarkWhenIdle`
 * relance le marquage dès que la lecture en cours d'une autre vue de la même conversation finit.
 */
function useReadRequest(markRead: boolean): {
  take: () => boolean
  request: () => void
  failed: (marked: boolean) => void
  armed: () => boolean
} {
  const armed = useRef(markRead)
  const wasMarking = useRef(markRead)
  if (markRead && !wasMarking.current) armed.current = true
  wasMarking.current = markRead
  return {
    take: () => {
      const read = armed.current
      armed.current = false
      return read
    },
    request: () => {
      armed.current = true
    },
    failed: (marked) => {
      if (marked) armed.current = true
    },
    armed: () => armed.current,
  }
}

/**
 * Le marquage armé n'a pas trouvé de lecture à porter : une lecture de la même conversation était
 * déjà en cours au montage (lancée par une autre vue, sans marquage), et react-query ne relit pas
 * tout de suite. Dès qu'elle finit sans erreur, relire pour marquer, sans attendre le tour suivant.
 * Après un échec, le marquage attend le tour suivant (pas de boucle de relectures).
 */
function useMarkWhenIdle(
  read: { armed: () => boolean },
  query: { isFetching: boolean; isError: boolean; refetch: () => Promise<unknown> },
  enabled: boolean,
): void {
  const { isFetching, isError, refetch } = query
  const wasFetching = useRef(isFetching)
  useEffect(() => {
    // Seulement à la fin d'une lecture : un marquage armé par un clic part avec sa propre relecture.
    const justFinished = wasFetching.current && !isFetching
    wasFetching.current = isFetching
    if (enabled && justFinished && !isError && read.armed()) void refetch()
  }, [enabled, isFetching, isError, read, refetch])
}

// ── Compteurs ────────────────────────────────────────────────────────────────

export function useUgcOverview(appId: string | null) {
  return useQuery<UgcOverview, Error>({
    queryKey: ugcKeys.overview(appId ?? ''),
    queryFn: () => api.get<UgcOverview>(`/api/ugc/overview${query({ appId: appId ?? '' })}`),
    enabled: Boolean(appId),
    ...LIVE,
  })
}

// ── Candidatures ─────────────────────────────────────────────────────────────

export function useUgcApplications(status: ApplicationStatus) {
  return useQuery<AdminApplication[], Error>({
    queryKey: ugcKeys.applications(status),
    queryFn: () =>
      api.get<{ applications: AdminApplication[] }>(`/api/ugc/applications${query({ status })}`).then((r) => r.applications),
  })
}

/** Accepte une candidature : rend le membre créé (et son contact du CRM, `creatorId`). */
/** Accepte une candidature ; `rateCentsPerThousand` (facultatif) fixe aussitôt le montant pour 1 000 vues. */
export function useAcceptApplication() {
  return useUgcMutation<{ member: UgcMember }, { id: string; rateCentsPerThousand?: number | undefined }>(
    ({ id, rateCentsPerThousand }) =>
      api.post(
        `/api/ugc/applications/${encodeURIComponent(id)}/accept`,
        rateCentsPerThousand === undefined ? {} : { rateCentsPerThousand },
      ),
  )
}

/** Refuse une candidature ; `message` (facultatif) est ajouté à l'e-mail de refus. */
export function useRejectApplication() {
  return useUgcMutation<{ application: UgcApplication }, { id: string; message: string | null }>(({ id, message }) =>
    api.post(`/api/ugc/applications/${encodeURIComponent(id)}/reject`, { message }),
  )
}

// ── Vidéos ───────────────────────────────────────────────────────────────────

export function useUgcVideos(view: UgcVideosView, filters: UgcVideosFilters = {}) {
  return useQuery<AdminVideoQueueItem[], Error>({
    queryKey: ugcKeys.videos(view, filters),
    queryFn: () =>
      api
        .get<{ items: AdminVideoQueueItem[] }>(
          `/api/ugc/videos${query({ view, memberId: filters.memberId, status: filters.status })}`,
        )
        .then((r) => r.items),
  })
}

export function useUgcVideo(id: string | null) {
  return useQuery<AdminVideoDetail, Error>({
    queryKey: ugcKeys.video(id ?? ''),
    queryFn: () => api.get<AdminVideoDetail>(`/api/ugc/videos/${encodeURIComponent(id ?? '')}`),
    enabled: Boolean(id),
  })
}

/**
 * Décision sur une idée ou un brouillon ; commentaire obligatoire pour une correction. `highlights`
 * (« Ce qui marche ») et `fixes` (« À retoucher ») : une phrase par point.
 */
export function useReviewVideo() {
  return useUgcMutation<
    { video: UgcVideo },
    {
      id: string
      stage: ReviewStage
      decision: ReviewDecision
      comment: string | null
      highlights: string[]
      fixes: string[]
      /** Idée telle qu'affichée : obligatoire pour une décision sur l'idée (409 si elle a changé). */
      seen?: { title: string; idea: string; hook: string }
    }
  >(({ id, ...body }) => api.post(`/api/ugc/videos/${encodeURIComponent(id)}/review`, body))
}

/**
 * Fil d'une vidéo (conversation comprise). `markRead` : la vidéo a été choisie (clic, lien d'une
 * alerte) : la lecture qui suit ce choix la marque lue côté équipe (`?read=1`), et `refetchRead()`
 * (nouveau clic) relit en la marquant ; quand elle avait des messages non lus, les listes de vidéos
 * sont relues pour que leur pastille disparaisse. Les relectures automatiques (toutes les 30 s) ne
 * marquent jamais rien. La clé ne dépend pas de `markRead` : relire ne repasse pas par le chargement.
 */
export function useUgcThread(id: string | null, opts: { markRead?: boolean } = {}) {
  const queryClient = useQueryClient()
  const read = useReadRequest(opts.markRead === true)
  const result = useQuery<AdminVideoThread, Error>({
    queryKey: ugcKeys.thread(id ?? ''),
    queryFn: async () => {
      const markRead = read.take()
      const path = `/api/ugc/videos/${encodeURIComponent(id ?? '')}/thread${markRead ? '?read=1' : ''}`
      let thread: AdminVideoThread
      try {
        thread = await api.get<AdminVideoThread>(path)
      } catch (err) {
        read.failed(markRead)
        throw err
      }
      if (markRead && thread.unread > 0) void queryClient.invalidateQueries({ queryKey: ugcKeys.videoLists })
      return thread
    },
    enabled: Boolean(id),
    ...LIVE,
  })
  useMarkWhenIdle(read, result, Boolean(id))
  return Object.assign(result, {
    refetchRead: () => {
      read.request()
      return result.refetch()
    },
  })
}

/**
 * Message de l'équipe à la personne ; `email` : l'e-mail « Nouveau message » est parti, ou pourquoi
 * il n'est pas parti (un autre il y a moins de 30 minutes, plafond du jour, échec).
 */
export function useSendTeamMessage() {
  return useUgcMutation<{ message: UgcMessage; email: ConversationEmailOutcome }, { id: string; body: string }>(
    ({ id, body }) => api.post(`/api/ugc/videos/${encodeURIComponent(id)}/messages`, { body }),
  )
}

// ── Chat avec l'équipe ───────────────────────────────────────────────────────

/** Membres dont le chat a au moins un message, non-lus en tête (onglet « Messages »). */
export function useUgcChats() {
  return useQuery<AdminChatListItem[], Error>({
    queryKey: ugcKeys.chats,
    queryFn: () => api.get<{ chats: AdminChatListItem[] }>('/api/ugc/chats').then((r) => r.chats),
    ...LIVE,
  })
}

/**
 * Chat d'un membre. `markRead` : le membre a été choisi (clic, lien d'une alerte, « Écrire à un
 * membre ») : la lecture qui suit ce choix marque le chat lu côté équipe (`?read=1`), et
 * `refetchRead()` (nouveau clic) relit en le marquant ; quand il avait des messages non lus, la
 * liste et les compteurs (pastille de l'onglet, Cockpit) sont relus. Les relectures automatiques
 * (toutes les 30 s) ne marquent jamais rien.
 */
export function useUgcChat(memberId: string | null, opts: { markRead?: boolean } = {}) {
  const queryClient = useQueryClient()
  const read = useReadRequest(opts.markRead === true)
  const result = useQuery<AdminChatThread, Error>({
    queryKey: ugcKeys.chat(memberId ?? ''),
    queryFn: async () => {
      const markRead = read.take()
      const path = `/api/ugc/chats/${encodeURIComponent(memberId ?? '')}${markRead ? '?read=1' : ''}`
      let thread: AdminChatThread
      try {
        thread = await api.get<AdminChatThread>(path)
      } catch (err) {
        read.failed(markRead)
        throw err
      }
      if (markRead && thread.unread > 0) {
        void queryClient.invalidateQueries({ queryKey: ugcKeys.chats })
        void queryClient.invalidateQueries({ queryKey: ['ugc', 'overview'] })
      }
      return thread
    },
    enabled: Boolean(memberId),
    ...LIVE,
  })
  useMarkWhenIdle(read, result, Boolean(memberId))
  return Object.assign(result, {
    refetchRead: () => {
      read.request()
      return result.refetch()
    },
  })
}

/** Message de l'équipe dans le chat d'un membre ; `email` : l'e-mail est parti, ou pourquoi pas. */
export function useSendChatMessage() {
  return useUgcMutation<{ message: UgcChatMessage; email: ConversationEmailOutcome }, { memberId: string; body: string }>(
    ({ memberId, body }) => api.post(`/api/ugc/chats/${encodeURIComponent(memberId)}/messages`, { body }),
  )
}

// ── Publications ─────────────────────────────────────────────────────────────

export function useUgcPublications(view: UgcPublicationsView) {
  return useQuery<AdminPublicationItem[], Error>({
    queryKey: ugcKeys.publications(view),
    queryFn: () =>
      api.get<{ items: AdminPublicationItem[] }>(`/api/ugc/publications${query({ view })}`).then((r) => r.items),
  })
}

/** Valide une capture (ou un relevé sans capture) ; `payout` = montant figé s'il vient de l'être. */
export function useValidateReading() {
  return useUgcMutation<
    { publication: UgcPublication; payout: UgcPayout | null },
    { id: string; views: number; withoutScreenshot: boolean }
  >(({ id, ...body }) => api.post(`/api/ugc/publications/${encodeURIComponent(id)}/validate`, body))
}

/** Demande une autre capture (commentaire obligatoire). */
export function useRejectScreenshot() {
  return useUgcMutation<{ publication: UgcPublication }, { id: string; comment: string }>(({ id, comment }) =>
    api.post(`/api/ugc/publications/${encodeURIComponent(id)}/reject-screenshot`, { comment }),
  )
}

/** Corrige un relevé validé, tant que le montant n'est pas rattaché à un règlement. */
export function useCorrectReading() {
  return useUgcMutation<
    { publication: UgcPublication; payout: UgcPayout | null; referral: CorrectionReferral },
    { id: string; views: number }
  >(({ id, views }) => api.post(`/api/ugc/publications/${encodeURIComponent(id)}/correct`, { views }))
}

/** Supprime une publication sans relevé validé ; `payout` = montant figé s'il vient de l'être. */
export function useDeletePublication() {
  return useUgcMutation<{ ok: true; payout: UgcPayout | null }, string>((id) =>
    api.del(`/api/ugc/publications/${encodeURIComponent(id)}`),
  )
}

// ── Paiements et règlements ──────────────────────────────────────────────────

export function useUgcPayments() {
  return useQuery<AdminPaymentsView, Error>({
    queryKey: ugcKeys.payments,
    queryFn: () => api.get<AdminPaymentsView>('/api/ugc/payments'),
  })
}

/** « Facture reçue » (`invoice`) ou « Réglé autrement » (`arrangement`) pour les éléments cochés. */
export function useCreateSettlement() {
  return useUgcMutation<
    { settlement: UgcSettlement },
    { memberId: string; kind: SettlementKind; payoutIds: string[]; bonusIds: string[]; note: string | null }
  >((body) => api.post('/api/ugc/settlements', body))
}

/** « Payé » à la date `paidOn` (AAAA-MM-JJ) : ajoute la dépense du règlement. */
export function useMarkSettlementPaid() {
  return useUgcMutation<{ settlement: UgcSettlement }, { id: string; paidOn: string }>(({ id, paidOn }) =>
    api.post(`/api/ugc/settlements/${encodeURIComponent(id)}/paid`, { paidOn }),
  )
}

/** Annule un règlement : ses éléments redeviennent dus, sa dépense est supprimée. */
export function useCancelSettlement() {
  return useUgcMutation<{ ok: true }, string>((id) => api.del(`/api/ugc/settlements/${encodeURIComponent(id)}`))
}

// ── Parrainages ──────────────────────────────────────────────────────────────

export function useUgcReferrals() {
  return useQuery<AdminReferralItem[], Error>({
    queryKey: ugcKeys.referrals,
    queryFn: () => api.get<{ items: AdminReferralItem[] }>('/api/ugc/referrals').then((r) => r.items),
  })
}

// ── Réglages ─────────────────────────────────────────────────────────────────

export function useUgcSettings() {
  return useQuery<UgcSettings, Error>({
    queryKey: ugcKeys.settings,
    queryFn: () => api.get<UgcSettings>('/api/ugc/settings'),
  })
}

export function useSaveUgcSettings() {
  return useUgcMutation<
    UgcSettings,
    { invoiceEmail: string | null; alertEmail: string | null; companyDetails: string | null }
  >((body) => api.put('/api/ugc/settings', body))
}

// ── Membres ──────────────────────────────────────────────────────────────────

/** Membres du programme, les plus récents d'abord ; `creatorId` limite au membre d'un contact. */
export function useUgcMembers(creatorId?: string) {
  return useQuery<UgcMember[], Error>({
    queryKey: ugcKeys.members(creatorId ?? null),
    queryFn: () => api.get<{ members: UgcMember[] }>(`/api/ugc/members${query({ creatorId })}`).then((r) => r.members),
  })
}

/** Invite au portail un contact `ugc_creator` qui n'est pas membre. */
export function useInviteMember() {
  return useUgcMutation<{ member: UgcMember }, { creatorId: string; email: string; canInvoice: boolean }>((body) =>
    api.post('/api/ugc/members/invite', body),
  )
}

export function usePatchMember() {
  return useUgcMutation<{ member: UgcMember }, { id: string; patch: MemberPatch }>(({ id, patch }) =>
    api.patch(`/api/ugc/members/${encodeURIComponent(id)}`, patch),
  )
}

/**
 * Fixe ou change le montant pour 1 000 vues d'un membre (centimes). `expectedRateCentsPerThousand` :
 * le montant affiché (`null` : pas encore fixé), 409 s'il a changé ailleurs. Le premier montant
 * prévient la personne par e-mail ; un changement seulement avec `notify`. `frozen` : vidéos qui attendaient ce
 * montant, figées par la même requête.
 */
export function useSetMemberRate() {
  return useUgcMutation<
    { member: UgcMember; emailed: boolean; frozen: number },
    { id: string; rateCentsPerThousand: number; expectedRateCentsPerThousand: number | null; notify: boolean }
  >(({ id, rateCentsPerThousand, expectedRateCentsPerThousand, notify }) =>
    api.patch(`/api/ugc/members/${encodeURIComponent(id)}/rate`, { rateCentsPerThousand, expectedRateCentsPerThousand, notify }),
  )
}

/**
 * Efface les données du portail d'un membre. S'il reste des sommes dues, l'API répond 409
 * `amounts_due` (ApiClientError, montants dans le message) : relancer avec `confirmDue: true`.
 */
export function useEraseMember() {
  return useUgcMutation<{ ok: true; deletedObjects: number }, { id: string; confirmDue?: boolean }>(({ id, confirmDue }) =>
    api.del(`/api/ugc/members/${encodeURIComponent(id)}${confirmDue ? '?confirmDue=1' : ''}`),
  )
}
